import {
    ComposedChart, Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    ReferenceLine,
} from 'recharts';
import type { JanelaBase, LeadsBaseAlcancavel } from '@/types/leadsDashboard';
import { diaCurto, n, pct } from '../format';

/**
 * Bloco 5.4 — Base alcançável por broadcast.
 *
 * Três leituras, da mais rápida para a mais detalhada: o número de hoje com a
 * variação da janela; a curva do estoque com a perda 551 do dia em barras no
 * pé; e a ponte início → entrou → 551 → outros → hoje, que diz POR QUE a base
 * mudou.
 */

const JANELAS: JanelaBase[] = [7, 15, 30, 60, 90];

function Tip({ active, payload, label }: any) {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
        <div className="tt">
            <div className="tt-t">{label}</div>
            <div className="tt-r">
                <span style={{ color: 'var(--violet)' }}>Alcançáveis</span>
                <span className="num" style={{ fontWeight: 600 }}>{n(p.alcancaveis)}</span>
            </div>
            {p.captados != null && (
                <div className="tt-r">
                    <span style={{ color: 'var(--blue)' }}>Entraram</span>
                    <span className="num" style={{ fontWeight: 600 }}>+{n(p.captados)}</span>
                </div>
            )}
            <div className="tt-r">
                <span style={{ color: 'var(--red)' }}>Perdidos 551</span>
                <span className="num" style={{ fontWeight: 600 }}>
                    −{n(p.perdidos_551)} · {pct(p.taxa_perda_pct, 2)}
                </span>
            </div>
        </div>
    );
}

interface Props {
    dados: LeadsBaseAlcancavel;
    /** null = segue o período do topo. */
    janela: JanelaBase | null;
    onJanela: (j: JanelaBase) => void;
}

export default function GraficoBaseAlcancavel({ dados, janela, onJanela }: Props) {
    const serie = dados.serie.map(p => ({ ...p, rotulo: diaCurto(p.data) }));
    const cresce = dados.ganho_liquido >= 0;
    const cor = cresce ? '#12805C' : '#C0392B';

    // Eixo com zoom: começando do zero, um estoque de 500 mil que varia 20 mil
    // vira uma faixa chata e a tendência some.
    const valores = [...serie.map(s => s.alcancaveis), ...(dados.ponte ? [dados.ponte.inicio] : [])];
    const min = Math.min(...valores);
    const max = Math.max(...valores);
    const folga = Math.max((max - min) * 0.15, max * 0.01, 1);
    const dominio: [number, number] = [Math.max(0, Math.floor(min - folga)), Math.ceil(max + folga)];

    // As barras de perda ocupam só o quarto de baixo, em eixo próprio e oculto.
    const maxPerda = Math.max(1, ...serie.map(s => s.perdidos_551));

    const ponte = dados.ponte;
    // API antiga não manda ate_hoje: o bloco sempre terminava hoje.
    const ateHoje = dados.ate_hoje ?? true;
    const rotuloFim = ateHoje || !dados.ate ? 'hoje' : `em ${diaCurto(dados.ate)}`;
    const maiorFluxo = ponte
        ? Math.max(1, ponte.entraram, ponte.perdidos_551, Math.abs(ponte.outros))
        : 1;

    return (
        <div className="card">
            <div className="card-h">
                <div>
                    <div className="card-t">Base alcançável por broadcast</div>
                    <div className="card-s">
                        Quanta gente ainda pode receber disparo, e o que fez esse número mudar.
                        {janela == null && ' Segue o período do topo.'}
                    </div>
                </div>
                <div className="seg">
                    {JANELAS.map(d => (
                        <button
                            key={d} type="button"
                            data-on={janela === d ? 1 : 0}
                            onClick={() => onJanela(d)}
                        >
                            {d}d
                        </button>
                    ))}
                </div>
            </div>

            {serie.length === 0 ? (
                <div className="empty">Sem histórico de base alcançável nesta janela ainda.</div>
            ) : (
                <div className="ba">
                    <div className="ba-hero">
                        <div className="eyebrow">Alcançáveis {rotuloFim}</div>
                        <div className="ba-v num">{n(dados.alcancaveis_hoje)}</div>
                        <div className="ba-chip" data-up={cresce ? 1 : 0}>
                            {cresce ? '▲' : '▼'} {pct(Math.abs(dados.crescimento_pct))}
                            <span>em {dados.janela_dias}d</span>
                        </div>
                        <div className="ba-linha">
                            <span>Ganho líquido</span>
                            <b className="num" style={{ color: cor }}>
                                {cresce ? '+' : '−'}{n(Math.abs(dados.ganho_liquido))}
                            </b>
                        </div>
                        <div className="ba-linha">
                            <span>Perda 551 por dia</span>
                            <b className="num" style={{ color: 'var(--red)' }}>
                                {pct(dados.taxa_perda_diaria_media_pct, 2)}
                            </b>
                        </div>
                    </div>

                    <div className="ba-chart">
                        <ResponsiveContainer width="100%" height={220}>
                            <ComposedChart data={serie} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="gBaseAlc" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={cor} stopOpacity={0.28} />
                                        <stop offset="100%" stopColor={cor} stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#F0F2F5" vertical={false} />
                                <XAxis
                                    dataKey="rotulo"
                                    tick={{ fontSize: 11, fill: '#8C93A0' }}
                                    axisLine={{ stroke: '#E3E6EB' }} tickLine={false}
                                    interval={Math.max(0, Math.floor(serie.length / 9))}
                                />
                                <YAxis
                                    yAxisId="l" domain={dominio} allowDataOverflow
                                    tick={{ fontSize: 11, fill: '#8C93A0' }}
                                    axisLine={false} tickLine={false} width={50}
                                    tickFormatter={(v: number) => `${Math.round(v / 1000)}k`}
                                />
                                <YAxis yAxisId="r" hide domain={[0, maxPerda * 4]} />
                                <Tooltip content={<Tip />} cursor={{ fill: 'rgba(109,74,199,.05)' }} />
                                <ReferenceLine
                                    yAxisId="l" y={dados.ponte?.inicio ?? serie[0].alcancaveis}
                                    stroke="#C9CFD8" strokeDasharray="3 3"
                                />
                                <Bar
                                    yAxisId="r" dataKey="perdidos_551" name="Perdidos 551"
                                    fill="#C0392B" fillOpacity={0.35} radius={[2, 2, 0, 0]} maxBarSize={14}
                                />
                                <Area
                                    yAxisId="l" type="monotone" dataKey="alcancaveis" name="Alcançáveis"
                                    stroke={cor} strokeWidth={2.2} fill="url(#gBaseAlc)"
                                    dot={false} activeDot={{ r: 4 }}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                        <div className="ba-leg">
                            <span><i style={{ background: cor }} />Alcançáveis</span>
                            <span><i style={{ background: '#C0392B', opacity: 0.5 }} />Perdidos 551 no dia</span>
                            <span><i className="ba-tr" />Início da janela</span>
                        </div>
                    </div>
                </div>
            )}

            {ponte && (
                <div className="ponte">
                    <div className="ponte-i" data-k="base">
                        <span>
                            {ponte.inicio_data ? `Fim de ${diaCurto(ponte.inicio_data)}` : `Há ${dados.janela_dias - 1}d`}
                        </span>
                        <b className="num">{n(ponte.inicio)}</b>
                    </div>
                    <div className="ponte-op">+</div>
                    <div className="ponte-i" data-k="in">
                        <span>Entraram</span>
                        <b className="num">{n(ponte.entraram)}</b>
                        <i style={{ width: `${(ponte.entraram / maiorFluxo) * 100}%` }} />
                    </div>
                    <div className="ponte-op">−</div>
                    <div className="ponte-i" data-k="out">
                        <span>Perdidos 551</span>
                        <b className="num">{n(ponte.perdidos_551)}</b>
                        <i style={{ width: `${(ponte.perdidos_551 / maiorFluxo) * 100}%` }} />
                    </div>
                    <div className="ponte-op">{ponte.outros >= 0 ? '+' : '−'}</div>
                    <div
                        className="ponte-i" data-k="outros"
                        title="Resíduo que fecha a conta: lead desativado ou reativado à mão, lead apagado."
                    >
                        <span>Outros ajustes</span>
                        <b className="num">{n(Math.abs(ponte.outros))}</b>
                        <i style={{ width: `${(Math.abs(ponte.outros) / maiorFluxo) * 100}%` }} />
                    </div>
                    <div className="ponte-op">=</div>
                    <div className="ponte-i" data-k="base">
                        <span>{ateHoje ? 'Hoje' : `Fim de ${diaCurto(dados.ate!)}`}</span>
                        <b className="num" style={{ color: cor }}>{n(ponte.fim)}</b>
                    </div>
                </div>
            )}
        </div>
    );
}
